import { Fragment, ReactElement, useCallback, useEffect } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Button from '../../components/Button';
import { FitBounds, BoundsListener } from '../../components/MapBoundsComponents';
import { useGatesWMinRoute } from '../../hooks/useGates';
import { useGateSummaries, useGateFocus, useMapBounds } from '../../hooks/useGateMap';
import { createGateIcon } from '../../utils/gateMapUtils';
import markerIconPng from '../../icons/marker-icon-green.png';
import endMarkerIconPng from '../../icons/marker-icon-red.png';
import { GateSummary } from '../../types/GateSummary';
import { useLocation, useNavigate } from 'react-router-dom';

type RouteSelectionState = {
  returnTo?: string;
  selectedGateId?: string | null;
  sessionId?: string; // When coming from session view
  hasCallback?: boolean; // Indicates if a callback function is available
};

export default function RouteSelection(): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state as RouteSelectionState | null;
  const returnTo = routeState?.returnTo;
  const selectedGateIdFromState = routeState?.selectedGateId ?? null;
  const sessionId = routeState?.sessionId; // Session ID if coming from session view

  const { data: gates, isPending, isError } = useGatesWMinRoute();

  const gateSummaries = useGateSummaries(gates);

  const {
    activeGateId,
    setActiveGateId,
    focusMode,
    setFocusMode,
    selectedGate,
    fitBoundsTrigger,
    handleFocusGate,
    handleZoomAll,
  } = useGateFocus(gateSummaries, {
    initialGateId: selectedGateIdFromState,
    initialFocusMode: selectedGateIdFromState ? 'selected' : 'all',
  });

  const { bounds, mapCenter, showBothGates, handleMapBoundsChange } = useMapBounds(
    gateSummaries,
    selectedGate,
    focusMode,
  );

  useEffect(() => {
    if (!selectedGateIdFromState) return;
    setActiveGateId((prev) => (prev === selectedGateIdFromState ? prev : selectedGateIdFromState));
    setFocusMode('selected');
  }, [selectedGateIdFromState, setActiveGateId, setFocusMode]);

  const getGateIcon = useCallback(
    (type: 'start' | 'end', label: string) => createGateIcon(type, label, markerIconPng, endMarkerIconPng),
    [],
  );

  const handleSelectGate = useCallback(
    (summary: GateSummary) => {
      handleFocusGate(summary.id);

      // Check if there's a callback function to call
      if (routeState?.hasCallback && (window as any).__gateSelectCallback) {
        // Call the callback directly
        (window as any).__gateSelectCallback(summary.gate.id);
        // Navigate back without state
        if (returnTo) {
          navigate(returnTo);
        }
      } else if (returnTo) {
        // Fallback to old behavior for backward compatibility
        navigate(returnTo, { state: { selectedGateId: summary.gate.id, fromSession: !!sessionId } });
      }
    },
    [handleFocusGate, navigate, returnTo, sessionId, routeState?.hasCallback],
  );

  const handleCreateNew = useCallback(() => {
    // Find current gate lat/lon from the selected gate (if any)
    const currentGate = selectedGateIdFromState
      ? gateSummaries.find((g) => g.id === selectedGateIdFromState)?.gate
      : undefined;

    navigate('/gate-from-run', {
      state: {
        returnTo,
        sessionId,
        existingGatesLat: currentGate?.gates_lat,
        existingGatesLon: currentGate?.gates_lon,
        hasCallback: routeState?.hasCallback,
      },
    });
  }, [navigate, returnTo, sessionId, selectedGateIdFromState, gateSummaries, routeState?.hasCallback]);

  const backLink = returnTo ? { to: returnTo, text: sessionId ? 'Back to Session' : 'Back to Calibration' } : undefined;

  const headerActions = sessionId ? (
    <button
      type="button"
      onClick={handleCreateNew}
      className="cr-rounded-md cr-bg-cirrusBlue-100 cr-px-3 cr-py-1.5 cr-text-sm cr-font-semibold cr-text-white cr-transition-colors hover:cr-bg-cirrusBlue-200"
      style={{ fontFamily: 'Poppins, sans-serif' }}
    >
      Create New
    </button>
  ) : undefined;

  return (
    <div className="cr-w-full">
      <SectionHeader title="Route Selection" isLoading={isPending} backLink={backLink} actions={headerActions} />

      <div className="cr-mt-6 cr-grid cr-gap-6">
        <div className="cr-relative cr-h-[500px] cr-overflow-hidden cr-rounded-xl cr-bg-white cr-shadow-sm">
          {isPending && (
            <div className="cr-flex cr-h-full cr-items-center cr-justify-center">
              <Spinner isFetching={true} />
            </div>
          )}

          {isError && !isPending && (
            <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-text-red-600">
              Unable to load gates for this team. Please try again later.
            </div>
          )}

          {!isError && !isPending && gateSummaries.length === 0 && (
            <div className="cr-flex cr-h-full cr-items-center cr-justify-center">
              No gates available for this team yet.
            </div>
          )}

          {!isError && !isPending && gateSummaries.length > 0 && (
            <>
              {selectedGate && gateSummaries.length > 1 && (
                <div className="cr-absolute cr-right-4 cr-top-4 cr-z-[1000]">
                  <button
                    type="button"
                    onClick={handleZoomAll}
                    className="cr-rounded-md cr-bg-white cr-px-3 cr-py-1 cr-text-xs cr-font-semibold cr-text-[#03365D] cr-shadow-sm hover:cr-bg-cirrusBlue-25"
                  >
                    All Gates
                  </button>
                </div>
              )}
              <MapContainer center={mapCenter} zoom={13} scrollWheelZoom className="cr-h-full cr-w-full">
                <FitBounds bounds={bounds} trigger={fitBoundsTrigger} />
                <BoundsListener onBoundsChange={handleMapBoundsChange} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {gateSummaries.map((marker) => (
                  <Fragment key={marker.id}>
                    <Marker position={marker.start} icon={getGateIcon('start', String(marker.order))}>
                      <Popup>
                        <div className="cr-min-w-[180px]">
                          <p className="cr-font-semibold">{marker.popupLabel}</p>
                          <p className="cr-text-xs cr-text-gray-500">Gate #{marker.order}</p>
                          <p className="cr-text-xs cr-text-gray-500">Start gate</p>
                          <p className="cr-text-sm cr-text-gray-600">
                            {marker.isValid ? 'Valid' : 'Not yet validated'}
                          </p>
                        </div>
                      </Popup>
                    </Marker>
                    {showBothGates && marker.end && (
                      <Marker position={marker.end} icon={getGateIcon('end', String(marker.order))}>
                        <Popup>
                          <div className="cr-min-w-[180px]">
                            <p className="cr-font-semibold">{marker.popupLabel}</p>
                            <p className="cr-text-xs cr-text-gray-500">Gate #{marker.order}</p>
                            <p className="cr-text-xs cr-text-gray-500">End gate</p>
                            <p className="cr-text-sm cr-text-gray-600">
                              {marker.isValid ? 'Valid' : 'Not yet validated'}
                            </p>
                          </div>
                        </Popup>
                      </Marker>
                    )}
                  </Fragment>
                ))}
              </MapContainer>
            </>
          )}
        </div>

        <div className="cr-rounded-xl cr-bg-white cr-p-6 cr-shadow-sm">
          <h2 className="cr-mb-2 cr-text-lg cr-font-semibold">Gate Pairs</h2>
          <p className="cr-mb-4 cr-text-sm cr-text-gray-600">
            Gate pairs are numbered for quick reference. Use Center to focus the map or Select to apply the gates.
          </p>

          <div className="cr-flex cr-flex-col cr-gap-3">
            {gateSummaries.map((marker) => {
              const isActive = marker.id === activeGateId;
              return (
                <div
                  key={marker.id}
                  onClick={() => handleFocusGate(marker.id)}
                  className={`cr-flex cr-cursor-pointer cr-items-center cr-justify-between
                  cr-rounded-lg cr-border cr-p-4 cr-transition-colors ${
                    isActive ? 'cr-border-cirrusBlue-200 cr-bg-cirrusBlue-25' : 'cr-border-gray-200'
                  }`}
                >
                  <div className="cr-flex cr-items-start cr-gap-3">
                    <span className="cr-flex cr-h-6 cr-w-6 cr-items-center cr-justify-center cr-rounded-full cr-bg-cirrusBlue-100 cr-text-xs cr-font-semibold cr-text-white">
                      {marker.order}
                    </span>
                    <div>
                      <p className="cr-font-medium">{marker.label}</p>
                      {marker.routeDescription && (
                        <p className="cr-text-xs cr-text-gray-500">{marker.routeDescription}</p>
                      )}
                      <p className="cr-text-xs cr-text-gray-500">ID: {marker.id}</p>
                    </div>
                  </div>
                  <div className="cr-flex cr-items-center cr-gap-2">
                    <Button
                      onClick={(event) => {
                        event.stopPropagation();
                        handleFocusGate(marker.id);
                      }}
                    >
                      Center
                    </Button>
                    <Button
                      onClick={(event) => {
                        event.stopPropagation();
                        handleSelectGate(marker);
                      }}
                    >
                      Select
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
